import { Formik } from 'formik';
import { useCallback, useState } from 'react';
import { object } from 'yup';

import {
  CompositeControlFrame,
  ControlFrameCharacteristic,
  POSITION_SYMBOL,
  RUNES_CALLOUT_TYPES,
} from '@frontend/api';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex,
  VStack,
  GDTControlFrame,
  GDTSymbolText,
  Text,
} from '@frontend/components';

import { SubFormProps } from '../types';
import {
  ControlFrameFields,
  ControlFrameFormValues,
  initialControlFrameValues,
  controlFrameValidationSchema,
  convertToFeatureControlFrame,
  formatControlFrameSummary,
  convertToGDTControlFrameProps,
} from './ControlFrameForm';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { CharacteristicSelect } from './shared/TypedSelects';
import { createCharacteristicValidation } from './shared/validation';

type CompositeControlFrameFormValues = {
  characteristic: ControlFrameCharacteristic;
  patternTolerance: ControlFrameFormValues;
  featureTolerance: ControlFrameFormValues;
};

const initialValues: CompositeControlFrameFormValues = {
  characteristic: POSITION_SYMBOL,
  patternTolerance: { ...initialControlFrameValues },
  featureTolerance: { ...initialControlFrameValues },
};

const validationSchema = object().shape({
  characteristic: createCharacteristicValidation(),
  patternTolerance: controlFrameValidationSchema,
  featureTolerance: controlFrameValidationSchema,
});

export const CompositeControlFrameForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const [activeAccordionIndex, setActiveAccordionIndex] = useState<number>(0);

  const handleSubmit = useCallback(
    (values: CompositeControlFrameFormValues) => {
      // Convert form values to FeatureControlFrame objects
      const compositeControlFrame: CompositeControlFrame = {
        calloutType: RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME,
        controlFrames: [
          convertToFeatureControlFrame(values.patternTolerance),
          convertToFeatureControlFrame(values.featureTolerance),
        ],
        rawTexts: [
          formatControlFrameSummary(values.patternTolerance),
          formatControlFrameSummary(values.featureTolerance),
        ],
        subLocs: [], // TODO: what do we do here?
      };

      onSubmit(compositeControlFrame, compositeControlFrame.rawTexts.join(' '));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => (
        <SubFormWrapper
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          renderSelectedType={renderSelectedType}
          submitLabel="Add Composite Control Frame"
        >
          {/* Preview Section */}
          <FormPreviewSection>
            <VStack spacing={0} align="center">
              <GDTControlFrame
                isComposite={true}
                patternTolerance={convertToGDTControlFrameProps(
                  values.patternTolerance,
                )}
                featureTolerance={convertToGDTControlFrameProps(
                  values.featureTolerance,
                )}
              />
            </VStack>
          </FormPreviewSection>

          {/* Form Fields */}
          <Flex direction="column" gap={1} px={1}>
            <CharacteristicSelect
              name="characteristic"
              label="Characteristic"
              value={values.characteristic}
              onChange={(value) => {
                // update the characteristic for all children
                setFieldValue('characteristic', value);
                setFieldValue('patternTolerance.characteristic', value);
                setFieldValue('featureTolerance.characteristic', value);
              }}
            />
            {/* Control Frames Accordion */}
            <Accordion
              allowMultiple={false}
              allowToggle={true}
              index={activeAccordionIndex}
              onChange={(expandedIndex) => {
                if (typeof expandedIndex === 'number') {
                  setActiveAccordionIndex(expandedIndex);
                }
              }}
              variant="separate"
              size="md"
            >
              <AccordionItem>
                <AccordionButton>
                  <Flex justify="space-between" align="center" width="100%">
                    <Text fontWeight="medium">Pattern Tolerance</Text>
                    <GDTSymbolText>
                      {formatControlFrameSummary(values.patternTolerance)}
                    </GDTSymbolText>
                  </Flex>
                </AccordionButton>
                <AccordionPanel p={1}>
                  <Flex direction="column" gap={1}>
                    <ControlFrameFields
                      values={values.patternTolerance}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      touched={touched}
                      fieldPrefix={`patternTolerance.`}
                      includeCharacteristic={false}
                    />
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
              <AccordionItem>
                <AccordionButton>
                  <Flex justify="space-between" align="center" width="100%">
                    <Text fontWeight="medium">Feature Tolerance</Text>
                    <GDTSymbolText>
                      {formatControlFrameSummary(values.featureTolerance)}
                    </GDTSymbolText>
                  </Flex>
                </AccordionButton>
                <AccordionPanel p={1}>
                  <Flex direction="column" gap={1}>
                    <ControlFrameFields
                      values={values.featureTolerance}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      touched={touched}
                      fieldPrefix={`featureTolerance.`}
                      includeCharacteristic={false}
                    />
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Flex>
        </SubFormWrapper>
      )}
    </Formik>
  );
};
